import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { config } from "../../Config";
import { getIDToken } from "../firebase";

// Personal access tokens for the Platform API (/platform/v1). These endpoints only accept the
// signed-in browser session, never a token, and are not workspace-scoped (a token acts as the
// user in every workspace they belong to).

export const PLATFORM_BASE_URL = `${config.apiUrl}/platform/v1`;
export const PLATFORM_OPENAPI_URL = `${PLATFORM_BASE_URL}/openapi.json`;
export const PLATFORM_SKILL_URL = `${PLATFORM_BASE_URL}/skill.zip`;

export type PlatformToken = {
  id: string;
  name: string;
  tokenPrefix: string;
  expiresAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
};

export type CreatedPlatformToken = PlatformToken & { token: string };

export class PlatformApiError extends Error {
  constructor(message: string, public status: number, public code?: string) {
    super(message);
  }
}

const send = async <T>(path: string, method = "GET", body?: object): Promise<T> => {
  const token = await getIDToken();
  const res = await fetch(`${PLATFORM_BASE_URL}${path}`, {
    method,
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${token}`
    },
    body: body ? JSON.stringify(body) : undefined
  });

  const json = await res.json().catch(() => null);
  if (!res.ok) {
    throw new PlatformApiError(
      json?.error?.message ?? `Request failed (${res.status})`,
      res.status,
      json?.error?.code
    );
  }
  return json as T;
};

const TOKENS_KEY = ["platform", "me", "tokens"];

export const usePlatformTokens = () =>
  useQuery(TOKENS_KEY, () => send<{ items: PlatformToken[] }>("/me/tokens").then(res => res.items));

// Deliberately not a react-query mutation: the response holds the plaintext secret, and the
// MutationCache would keep it in memory for the rest of the session. The caller gets it once and
// keeps it only in component state.
export const useCreatePlatformToken = () => {
  const client = useQueryClient();
  return useCallback(
    async (input: { name: string; expiresInDays: number }) => {
      const created = await send<CreatedPlatformToken>("/me/tokens", "POST", input);
      await client.invalidateQueries(TOKENS_KEY);
      return created;
    },
    [client]
  );
};

export const useRevokePlatformToken = () => {
  const client = useQueryClient();
  return useMutation(
    (tokenId: string) => send<PlatformToken>(`/me/tokens/${encodeURIComponent(tokenId)}`, "DELETE"),
    { onSuccess: () => client.invalidateQueries(TOKENS_KEY) }
  );
};

export const tokenState = (token: PlatformToken, now = Date.now()) => {
  if (token.revokedAt) return "revoked" as const;
  if (new Date(token.expiresAt).getTime() <= now) return "expired" as const;
  return "active" as const;
};
